/**
 * The server's time as the shell knows it (ADR 0003, 0034): every API response carries `serverNow`, and the shell
 * draws countdowns from the last one it saw plus the time the page has run since (`performance.now()`, a monotonic
 * timer, never the device's clock). The client never sends a time the server trusts; this only draws.
 *
 * The one module that reads `Date` in `apps/web/src` (an ESLint rule bans `Date.now()` and `new Date()` elsewhere):
 * here, only to read the server's ISO timestamps (`Date.parse`). Until an answer has come, the time is unknown
 * (`known()` false) and a countdown shows nothing.
 *
 * Slice 7a's stand-in job clock (`?clock=`, `advance`) is gone with the stand-in store (ADR 0033 D3's "Expires"):
 * tests move the server's clock (`POST /api/test/clock`).
 */
export type ServerTime = {
  /** Takes the time a response says (`serverNow`, ISO 8601). */
  observe(serverNow: string): void;
  /** Whether a response has said the time yet. */
  known(): boolean;
  /** The server's time now, in whole seconds since the epoch (0 before any answer). */
  now(): number;
};

/** An ISO 8601 timestamp of the server as whole seconds since the epoch (NaN for a malformed one). */
export const secondsOf = (iso: string): number => Math.floor(Date.parse(iso) / 1000);

export const createServerTime = (): ServerTime => {
  let base: { readonly serverMs: number; readonly at: number } | null = null;
  return {
    observe(serverNow) {
      const ms = Date.parse(serverNow);
      if (!Number.isNaN(ms)) base = { serverMs: ms, at: performance.now() };
    },
    known: () => base !== null,
    now: () => (base === null ? 0 : Math.floor((base.serverMs + performance.now() - base.at) / 1000)),
  };
};
